import type { JourneyPageSectionRow } from "@/lib/api";

export type VerdictId = "ok" | "uwaga" | "do_poprawy" | "brak" | "poza_strona";

export type SectionAudit = {
  section: JourneyPageSectionRow;
  label: string;
  dropPp: number | null;
  verdict: VerdictId;
  reason: string;
  onPage: boolean;
};

const OFFER_SECTIONS = new Set([
  "cennik",
  "kontakt",
  "oferta",
  "kup",
  "final",
  "pakiet",
]);

const OUTSIDE_SID = "(poza sekcją)";

export function sectionLabel(
  sectionId: string,
  labels: Record<string, string> | undefined,
): string {
  return labels?.[sectionId] || sectionId;
}

function emptySection(sectionId: string, order: number): JourneyPageSectionRow {
  return {
    section_id: sectionId,
    order,
    sessions_reached: 0,
    view_rate: 0,
    cta_clicks: 0,
    cta_sessions: 0,
    cta_rate: 0,
    cta_rate_of_viewers: 0,
    ctas: [],
  };
}

/** Heurystyki OK / uwaga / do poprawy tylko dla sekcji na aktualnej stronie. */
export function buildSectionAudits(
  sections: JourneyPageSectionRow[],
  labels: Record<string, string> | undefined,
  options?: { onPageOnly?: boolean },
): SectionAudit[] {
  const onPageOnly = options?.onPageOnly !== false;
  const hasAnyCta = sections.some((s) => (s.cta_clicks ?? 0) > 0);
  const hero = sections.find((s) => s.section_id === "hero");
  const heroOk = (hero?.view_rate ?? 0) >= 0.7;

  return sections.map((section, index) => {
    const prev = index > 0 ? sections[index - 1] : null;
    const dropPp =
      prev != null && prev.view_rate != null && section.view_rate != null
        ? (prev.view_rate - section.view_rate) * 100
        : null;

    let verdict: VerdictId = "ok";
    let reason = "Zasięg i CTA w normie względem poprzedniej sekcji.";

    if (section.sessions_reached <= 0 && (section.view_rate ?? 0) <= 0) {
      verdict = "brak";
      reason = "Brak sygnału w tym okresie.";
    } else if (onPageOnly && dropPp != null && dropPp >= 25) {
      verdict = "do_poprawy";
      reason = `Duży spadek dojścia: −${Math.round(dropPp)} pp względem poprzedniej sekcji.`;
    } else if (
      onPageOnly &&
      hasAnyCta &&
      (section.view_rate ?? 0) >= 0.3 &&
      (section.cta_rate_of_viewers ?? 0) < 0.005 &&
      (section.cta_clicks ?? 0) === 0 &&
      OFFER_SECTIONS.has(section.section_id)
    ) {
      verdict = "do_poprawy";
      reason = "Sekcja ofertowa z sensownym reach, bez kliknięć CTA.";
    } else if (onPageOnly && dropPp != null && dropPp >= 12) {
      verdict = "uwaga";
      reason = `Spadek dojścia: −${Math.round(dropPp)} pp względem poprzedniej sekcji.`;
    } else if (
      onPageOnly &&
      heroOk &&
      section.section_id !== "hero" &&
      (section.view_rate ?? 0) > 0 &&
      (section.view_rate ?? 0) < 0.35 &&
      index >= 2 &&
      index < sections.length - 2
    ) {
      verdict = "uwaga";
      reason = "Słaby reach w środku lejka przy zdrowym hero.";
    } else if ((section.cta_clicks ?? 0) === 0 && !hasAnyCta) {
      reason = "Brak CTA na całej stronie w tym okresie; oceniamy tylko zasięg.";
    }

    return {
      section,
      label: sectionLabel(section.section_id, labels),
      dropPp,
      verdict,
      reason,
      onPage: true,
    };
  });
}

/**
 * Gdy structure.ok: kolejność live HTML + legacy z journey na dole.
 * Gdy structure niedostępna: zwykła lista journey (bez flagi poza stroną).
 */
export function mergeAuditsWithStructure(
  journeySections: JourneyPageSectionRow[],
  labels: Record<string, string> | undefined,
  structure: { ok: boolean; section_ids: string[] } | null | undefined,
): { onPage: SectionAudit[]; offPage: SectionAudit[]; structureOk: boolean } {
  const structureOk = Boolean(structure?.ok);
  const liveIds = structureOk ? structure!.section_ids : [];

  if (!structureOk) {
    const audits = buildSectionAudits(journeySections, labels).map((a) => ({
      ...a,
      onPage: true,
    }));
    return { onPage: audits, offPage: [], structureOk: false };
  }

  const byId = new Map(
    journeySections.map((s) => [s.section_id, s] as const),
  );

  const onPageRows: JourneyPageSectionRow[] = liveIds.map((sid, i) => {
    const existing = byId.get(sid);
    return existing
      ? { ...existing, order: i }
      : emptySection(sid, i);
  });

  const onPage = buildSectionAudits(onPageRows, labels, {
    onPageOnly: true,
  }).map((a) => ({ ...a, onPage: true }));

  const liveSet = new Set(liveIds);
  const offJourney = journeySections.filter(
    (s) => !liveSet.has(s.section_id),
  );
  const offPage: SectionAudit[] = offJourney.map((section, i) => ({
    section: { ...section, order: onPageRows.length + i },
    label: sectionLabel(section.section_id, labels),
    dropPp: null,
    verdict: "poza_strona" as const,
    reason:
      section.section_id === OUTSIDE_SID
        ? "Zdarzenia poza sekcją (tracker nie przypisał id)."
        : "Nie ma w obecnym HTML (stara wersja lub poza trackerem section/header).",
    onPage: false,
  }));

  return { onPage, offPage, structureOk: true };
}

export function iframeUrl(liveUrl: string, sectionId: string | null): string {
  const base = liveUrl.replace(/#.*$/, "");
  if (!sectionId || sectionId === OUTSIDE_SID) return base;
  return `${base}#${encodeURIComponent(sectionId)}`;
}
